/**
 * F-17-marks over a SELECTION OF CELLS (F-27), rather than over a character
 * range inside one — the "select these cells and hit B" gesture the grid needs,
 * because out there a writer has highlighted cells, not characters.
 *
 * The marks written are the ordinary per-range ones (@kola/shared
 * text-marks.ts), covering each cell's whole text. That is deliberate: a cell
 * whose every character is bold IS a bold cell, both renderers already paint
 * it, and formatting half of it afterwards — from the script view or from the
 * grid's own cell editor — just subtracts from the range, instead of fighting a
 * second whole-cell flag that only one of the two surfaces knew about.
 *
 * PURE — no Yjs, no DOM. `core/session.ts` reads the cells, calls these, and
 * writes the results in one transaction.
 */
import { applyMarkRange, clearMarkRange, rangeFullyMarked, type TextMark } from "@kola/shared";

/** One selected cell's text and its stored marks of the kind being toggled. */
export interface CellMarkTarget {
  text: string;
  marks: readonly TextMark[];
}

/**
 * Is every one of these cells already marked end to end?
 *
 * Cells with NO text are not counted either way — there is nothing in them for
 * a range to cover, so letting an empty cell in the corner of a selection
 * decide the answer would be arbitrary. An all-empty selection is `false`: it
 * is not marked, and the press that follows will find nothing to mark, which
 * is the same no-op read from both ends.
 *
 * Takes an ITERABLE and stops at the first cell that settles it, so a caller
 * reading cells out of the doc can hand over a lazy sequence rather than pay
 * for the whole selection to answer a question the first cell decides
 * (`session.ts markTargets`).
 */
export function everyCellFullyMarked(targets: Iterable<CellMarkTarget>): boolean {
  let sawText = false;
  for (const { text, marks } of targets) {
    if (text.length === 0) continue;
    sawText = true;
    if (!rangeFullyMarked(text, marks, 0, text.length)) return false;
  }
  return sawText;
}

/**
 * One cell's next mark list: its whole text marked, or its whole text cleared.
 *
 * `applyMarkRange` subtracts before it inserts, so a cell that was only PARTLY
 * marked ends up carrying exactly one span instead of its old ones plus a new
 * one — which is what "this whole cell is bold now" has to mean.
 */
export function wholeTextMarks(target: CellMarkTarget, on: boolean): TextMark[] {
  const { text, marks } = target;
  return on
    ? applyMarkRange(text, marks, 0, text.length)
    : clearMarkRange(text, marks, 0, text.length);
}
